import { useCallback, useMemo, useState } from "react"
import { Platform } from "react-native"
import { RelayEnvironmentProvider } from "react-relay"
import { createOperationDescriptor, Environment, FetchFunction, getRequest, Network, Store } from "relay-runtime"
import * as Sentry from "sentry-expo"

import { PuzmoClient } from "./apiClient"
import { RootAppQuery } from "../Puzzmo"
import { CurrentUser } from "../CoreContext"
import { CriticalFailureScreen } from "../screens/CriticalFailureScreen"

const fetcher =
  (client: PuzmoClient, networkExceptionSideEffect: (error: Error) => void): FetchFunction =>
  async (operation, variables) => {
    const headers: Record<string, string> = {}

    const wantsRelayLogs = typeof localStorage !== "undefined" && localStorage.getItem("showRelayLogs") === "true"

    let token = "~"
    if (client.authed()) {
      // try {
      //   const { exp } = await client.getTokenContents()

      //   // Checks if access token has expired and refresh tokens before proceeding
      //   if (!exp || exp * 1000 < Date.now()) {
      //     // console.warn(`starting refresh for ${operation.name}`)
      //     Sentry.Browser.addBreadcrumb({
      //       category: "auth",
      //       message: `Need to refresh for ${operation.name}`,
      //       level: "info",
      //     })
      //     await client.refresh()
      //     // console.warn(`done refresh for ${operation.name}`)
      //   }
      // } catch (err) {
      //   console.log("Error?", err)
      //   client.logout()
      // }

      const existingToken = await client.getToken()
      if (existingToken) token = existingToken
    }

    headers["auth-provider"] = "custom"
    headers["authorization"] = `Bearer ${token}`
    // debugger
    headers["puzzmo-gameplay-id"] = client.getUserID()

    // Relay offers a lot of info in the original outgoing request
    // { cacheID: "64f3434db5fcda977516dfe1b15852f7", id: null, metadata: {}, name: "UsersPageQuery", operationKind: "query", text: "...", variables: {} }
    //
    // We re-format it to fit the setup inside graphql-helix
    const body = {
      query: operation.text,
      operationName: operation.name,
      // operationKind: operation.operationKind,
      variables,
    }

    try {
      Sentry.Browser.addBreadcrumb({
        category: "api",
        message: `${operation.name}`,
        level: "info",
      })

      const response = await fetch(client.graphQL() + "?" + operation.name, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        credentials: "include",
      })

      const isDev = typeof location !== "undefined" && location.host.includes("localhost")
      if (isDev && wantsRelayLogs) {
        console.log(`↪️ ${operation.name}`, { request: body })
      }

      // Get the response as JSON
      const res = await response.json()

      if (isDev && wantsRelayLogs) {
        const logger = res.errors ? console.error : console.log
        const style = "font-weight: bold;"

        logger(`⬅️ %cResponse for ${operation.name}:`, style)
        logger("data" in res ? res.data : res)
        if (res.errors) {
          if (res.errors.length === 1) {
            logger("Error", res.errors[0])
          } else {
            logger("Errors", res.errors)
          }
        }
      }

      if (res.errors) {
        console.error(`Got errors in:'${operation.name}' `)
        if (res.errors.length === 1) {
          console.error("Error", res.errors[0])
        } else {
          console.error("Errors", res.errors)
        }
        const error = new Error(`Received unexpected errors from server: ${res.errors.map((e: any) => e.message).join(", ")}`)
        networkExceptionSideEffect(error)
      }

      return res
    } catch (error: any) {
      console.error(`Error in '${operation.name}'`, error)
      networkExceptionSideEffect(error)
      return { errors: [error] }
    }
  }

export const RelayProvider = (props: { store: Store; client: PuzmoClient; children: any }) => {
  const [error, setError] = useState<Error | null>(null)
  const gotException = useCallback((error: Error) => {
    if (error.message.includes("Network request failed") || error.message.includes("when attempting to fetch resource")) {
      error.message = "Could not contact Puzzmo server, are you offline?"
    }
    setError(error)
  }, [])

  const env = useMemo(() => {
    const env = new Environment({
      configName: "Main Relay Store",
      network: Network.create(fetcher(props.client, gotException || (() => {}))),
      store: props.store,
    })

    // We _don't_ want to make a lookup for the current user on launch, so we store the
    // user in local storage and load it here. You can get a general gist of the concept
    // (which is used in SSR) here https://dev.to/marais/relay-and-ssr-using-createoperationdescriptor-k6f

    if (Platform.OS === "web") {
      const existingLogin = localStorage.getItem("puzmoUserModel")
      if (existingLogin) {
        try {
          const cUser = JSON.parse(existingLogin) as CurrentUser
          const req = getRequest(RootAppQuery)
          const operationDescriptor = createOperationDescriptor(req, {})
          const payload = cUser
          env.commitPayload(operationDescriptor, payload)
          console.log("Loaded existing login from localstorage")
        } catch (error) {
          console.error("Error parsing existing login from localstorage", error)
        }
      }
    }

    return env
  }, [props.client, gotException, props.store])

  if (error) {
    return <CriticalFailureScreen errorMessage={error?.message} retry={() => setError(null)} />
  }
  return <RelayEnvironmentProvider environment={env}>{props.children}</RelayEnvironmentProvider>
}
